import { useCallback, useMemo, useState } from 'react';
import {
  TripResponse,
  useDeleteTripsByTripIdMutation,
  usePatchTripsByTripIdMutation,
} from '../../store/backendApi';

interface UseHeaderOptionsReturn {
  // Form state
  tripStartDate: string | null;
  tripEndDate: string | null;
  tripSortOrder: 'ASC' | 'DESC';
  interactionsEnabled: boolean;

  // Form handlers
  setTripDates: (startDate: string | null, endDate: string | null) => void;
  setTripSortOrder: (sortOrder: 'ASC' | 'DESC') => void;
  setInteractionsEnabled: (enabled: boolean) => void;

  // Operations
  updateTrip: () => Promise<void>;
  deleteTrip: (onDeleteComplete?: () => void) => Promise<void>;
  reset: () => void;

  // Status
  hasChanges: boolean;
  patchTripLoading: boolean;
  deleteTripLoading: boolean;
  deleteConfirmation: boolean;
  setDeleteConfirmation: (value: boolean) => void;
}

/**
 * Hook to manage trip settings (dates, sort order).
 * Title, description, and header image are managed inline in editTripHeader.
 * Platform-agnostic business logic.
 *
 * Handles:
 * - Trip dates and sort order
 * - Trip update and delete operations
 * - Change detection and form reset
 *
 * @param tripData - The trip data to manage
 * @returns Header options form state, handlers, and operations
 */
export const useHeaderOptions = (tripData: TripResponse): UseHeaderOptionsReturn => {
  // Form state (dates and sort order)
  const [tripStartDate, setTripStartDate] = useState<string | null>(tripData.startDate || null);
  const [tripEndDate, setTripEndDate] = useState<string | null>(tripData.endDate || null);
  const [tripSortOrder, setTripSortOrder] = useState<'ASC' | 'DESC'>(tripData.sortOrder);
  const [interactionsEnabled, setInteractionsEnabled] = useState<boolean>(
    tripData.interactionsEnabled ?? true,
  );

  // Delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  // API mutations
  const [patchTrip, { isLoading: patchTripLoading }] = usePatchTripsByTripIdMutation();
  const [deleteTripMutation, { isLoading: deleteTripLoading }] = useDeleteTripsByTripIdMutation();

  // Computed values
  const hasChanges = useMemo(() => {
    return (
      tripStartDate !== tripData.startDate ||
      tripEndDate !== tripData.endDate ||
      tripSortOrder !== tripData.sortOrder ||
      interactionsEnabled !== (tripData.interactionsEnabled ?? true)
    );
  }, [tripStartDate, tripEndDate, tripSortOrder, interactionsEnabled, tripData]);

  // Operations
  const updateTrip = useCallback(async () => {
    await patchTrip({
      tripId: tripData.id,
      partialTripBodyRequest: {
        startDate: tripStartDate || undefined,
        endDate: tripEndDate || undefined,
        sortOrder: tripSortOrder,
        interactionsEnabled: interactionsEnabled,
      },
    });
  }, [tripData.id, tripStartDate, tripEndDate, tripSortOrder, interactionsEnabled, patchTrip]);

  const reset = useCallback(() => {
    setTripStartDate(tripData.startDate || null);
    setTripEndDate(tripData.endDate || null);
    setTripSortOrder(tripData.sortOrder);
    setInteractionsEnabled(tripData.interactionsEnabled ?? true);
  }, [tripData]);

  const deleteTrip = useCallback(
    async (onDeleteComplete?: () => void) => {
      if (!deleteConfirmation) {
        setDeleteConfirmation(true);
        return;
      }

      await deleteTripMutation(tripData.id).unwrap();

      // Call the callback (e.g., navigation) after deletion
      if (onDeleteComplete) {
        onDeleteComplete();
      }
    },
    [deleteConfirmation, deleteTripMutation, tripData.id],
  );

  const setTripDates = useCallback((startDate: string | null, endDate: string | null) => {
    setTripStartDate(startDate);
    setTripEndDate(endDate);
  }, []);

  return {
    tripStartDate,
    tripEndDate,
    tripSortOrder,
    interactionsEnabled,
    setTripDates,
    setTripSortOrder,
    setInteractionsEnabled,
    updateTrip,
    deleteTrip,
    reset,
    hasChanges,
    patchTripLoading,
    deleteTripLoading,
    deleteConfirmation,
    setDeleteConfirmation,
  };
};
